import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useTranslation } from 'react-i18next';
import type { NavigationScreen } from 'app/types/navigation';
import type { ButtonProps } from 'app/design/primitives/button';
import { Button } from 'app/design/primitives/button';

import { type Tag, useAllTags } from 'app/network/listing';
import React, { useEffect } from 'react';
import { Platform, Pressable } from 'react-native';
import { useSetAtom } from 'jotai';
import { selectedTagAtom } from 'app/atoms';
import { usePageTitle } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

const TagsScreenComponent: NavigationScreen<'tags'> = ({ navigation, route }) => {
  const { t } = useTranslation('listings-filters');

  const setPageTitle = usePageTitle('tags');
  useEffect(() => setPageTitle('titleTags'), [setPageTitle]);

  const { tagsEnabled } = useListingCatalog();
  const { data: tags } = useAllTags();

  const setSelectedTag = useSetAtom(selectedTagAtom);

  useEffect(() => {
    if (!tagsEnabled) {
      navigation.replace('categories', { goBackTo: route.params.goBackTo });
    }
  }, [navigation, route.params.goBackTo, tagsEnabled]);

  const onTagSelected = (tagId: Tag['id']) => {
    setSelectedTag(tagId === route.params.selectedTag ? [] : [tagId]);
    navigation.goBack();
  };

  if (!tagsEnabled) return null;

  return (
    <Page isModal>
      <Column gap="spacing.general.x8">
        <Row>
          {Platform.OS === 'android' && (
            <Pressable
              accessibilityLabel={t('close', { ns: 'accessibility' })}
              accessibilityRole="button"
              onPress={() => navigation.goBack()}
            >
              <IconX strokeWidth={ICON_STROKE_WIDTH} />
            </Pressable>
          )}
          <Text flex={1} fontWeight="600" textAlign="center" variant="headerXSmall">
            {t('titleTags')}
          </Text>
        </Row>

        <Row flexWrap="wrap" gap="spacing.general.x3" rowGap="spacing.general.x3">
          {tags?.map((tag) => (
            <RenderedButton
              isSelected={route.params.selectedTag === tag.id}
              key={tag.id}
              onPress={() => onTagSelected(tag.id)}
              title={tag.tagName}
            />
          ))}
        </Row>
      </Column>
    </Page>
  );
};

export const TagsScreen: NavigationScreen<'tags'> = withErrorBoundary(TagsScreenComponent);

const RenderedButton = ({
  title,
  onPress,
  isSelected,
}: {
  title: string;
  onPress: ButtonProps['onPress'];
  isSelected?: boolean;
}) => {
  return (
    <Button
      onPress={onPress}
      style={{
        borderRadius: theme.borderRadii['radius.full'],
        backgroundColor: isSelected
          ? theme.colors['surface.inverse.tertiary-neutral']
          : theme.colors['surface.tertiary-neutral'],
        borderWidth: 1.5,
        borderColor: isSelected ? theme.colors['surface.inverse.tertiary-neutral'] : 'transparent',
        paddingHorizontal: theme.spacing['spacing.general.x4'],
        paddingVertical: theme.spacing['spacing.general.x2'],
        minHeight: 40,
      }}
      variant="plain"
    >
      <Text fontSize={15} fontWeight={isSelected ? '600' : '400'}>
        {title}
      </Text>
    </Button>
  );
};
